import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, Grid, TextField, CircularProgress, MenuItem, Select, FormControl, InputLabel, FormHelperText, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { toast } from 'react-toastify';
import { createLab, getLabType, updateLab } from "../../../services/mentorServices/MentorServices";

const convertTo24HourFormat = (time = '12') => {
  const [hour, minuteAndPeriod] = time?.split(':');
  const [minute, period] = minuteAndPeriod?.split(' ');

  let hour24 = parseInt(hour);
  if (period === 'PM' && hour24 !== 12) hour24 += 12;
  if (period === 'AM' && hour24 === 12) hour24 = 0;

  return `${hour24.toString().padStart(2, '0')}:${minute}`;
};

const convertTo12HourFormat = (time = '12') => {
  let [hour, minute] = time?.split(':');
  hour = parseInt(hour);
  const period = hour >= 12 ? 'PM' : 'AM';
  const newHour = hour % 12 || 12;
  return `${newHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')} ${period}`;
};

const validateTimeDifference = (start, end) => {
  const startDate = new Date(`1970-01-01T${start}:00`);
  const endDate = new Date(`1970-01-01T${end}:00`);
  return (endDate - startDate) >= 30 * 60 * 1000;
};

const INITIAL_FORM_STATE = { COE: '', labName: '', startTime: '', endTime: '', labType: '' };
const INITIAL_ERRORS_STATE = {};

const LabPracticeModal = ({ open, onClose, editRowData, getAllLabsData, allCOE }) => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState(INITIAL_ERRORS_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [labTypes, setLabTypes] = useState([])
  useEffect(() => {
    setFormValues(INITIAL_FORM_STATE);
    setFormErrors(INITIAL_ERRORS_STATE);

  }, [open]);
  useEffect(() => {
    getLabType()
      .then(response => setLabTypes(response?.data?.data.value || []))
      .catch(error => toast.error(error?.response?.data.message || error?.message || error?.error))
  }, []);

  useEffect(() => {
    if (editRowData) {
      setFormValues({
        COE: editRowData.location_id || '',
        labName: editRowData.lab_name || '',
        startTime: convertTo24HourFormat(editRowData.startTime) || '',
        endTime: convertTo24HourFormat(editRowData.endTime) || '',
        labType: editRowData.lab_type || ''
      });
    } else {
      setFormErrors(INITIAL_ERRORS_STATE);
    }
  }, [editRowData]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const validateFields = () => {
    const errors = {};
    const { COE, labName, startTime, endTime, labType } = formValues;

    if (!COE) errors.COE = 'COE is required*';
    if (!labName) errors.labName = 'Lab Name is required*';
    if (!startTime) errors.startTime = 'Start Time is required*';
    if (!endTime) errors.endTime = 'End Time is required*';
    if (startTime && endTime && !validateTimeDifference(startTime, endTime)) {
      errors.endTime = 'End Time must be at least 30 minutes after Start Time';
    }
    if (!labType) errors.labType = 'Lab Type is required*';

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateFields();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    const requestPayload = {
      lab_name: formValues.labName,
      location_id: formValues.COE,
      startTime: convertTo12HourFormat(formValues.startTime),
      endTime: convertTo12HourFormat(formValues.endTime),
      lab_type: formValues.labType
    };

    try {
      const response = editRowData
        ? await updateLab(editRowData.id, requestPayload)
        : await createLab(requestPayload);
      setFormValues(INITIAL_FORM_STATE);
      toast.success(response.data.message);
      getAllLabsData();

      onClose();
    } catch (error) {
      toast.error(error?.response?.data.message || error?.message || error?.error);
    } finally {
      setIsLoading(false);
    }
  };

  const formFields = [
    {
      label: 'COE *', name: 'COE', type: 'select', error: formErrors.COE,
      component: (
        <FormControl fullWidth size="small" error={!!formErrors.COE}>
          <InputLabel>COE*</InputLabel>
          <Select value={formValues.COE} onChange={handleInputChange} name="COE" label="COE">
            {allCOE?.map((data) => (
              <MenuItem key={data.id} value={data.id}>{data.name}</MenuItem>
            ))}
          </Select>
          {formErrors.COE && <FormHelperText>{formErrors.COE}</FormHelperText>}
        </FormControl>
      ),
    },
    {
      label: 'Lab Name *', name: 'labName', type: 'text', error: formErrors.labName
    },
    {
      label: 'Start Time *', name: 'startTime', type: 'time', error: formErrors.startTime
    },
    {
      label: 'End Time *', name: 'endTime', type: 'time', error: formErrors.endTime
    }
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '45%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {editRowData ? "Edit Lab " : "Create Lab "}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {formFields.map(({ label, name, type, error, component }) => (
              <Grid item xs={12} key={name}>
                {component || (
                  <TextField
                    fullWidth
                    size="small"
                    type={type}
                    name={name}
                    value={formValues[name]}
                    onChange={handleInputChange}
                    error={!!error}
                    helperText={error}
                    label={label}
                    InputLabelProps={type === 'time' ? { shrink: true } : undefined} />
                )}
              </Grid>
            ))}
            <Grid item xs={12}>
              <FormControl component="fieldset" error={!!formErrors.labType} sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center" }}>
                <Typography variant="body1" mr={3}>Lab Type *</Typography>
                <RadioGroup sx={{ justifyContent: "space-evenly" }} row name="labType" value={formValues.labType} onChange={handleInputChange}>
                  {labTypes?.map(labType => <FormControlLabel value={labType} control={<Radio />} label={labType} />
                  )}
                  {/* <FormControlLabel value="Fellowship" control={<Radio />} label="Fellowship" />
                  <FormControlLabel value="CodingClub" control={<Radio />} label="Coding Club" /> */}
                </RadioGroup>
              </FormControl>
              <FormHelperText sx={{ color: "#d32f2f", paddingLeft: "15px" }}>{formErrors.labType}</FormHelperText>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button type="submit" variant="contained" color="primary" disabled={isLoading} sx={{ marginRight: 2 }}>
                {isLoading ? <CircularProgress size={24} /> : editRowData ? "Update" : "Create"}
              </Button>
              <Button variant="outlined" color="secondary" onClick={onClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

export default LabPracticeModal;
